import * as actions from './alertsActions';

const initialState = {
  list: [],
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
  success: null,
  selectedAlert: null,
  unreadCount: 0,
  notifiedAlerts: [], // IDs of alerts that just triggered (for badge count)
  popoverAlertList: [], // Complete alert data for popover display
  filters: {
    status: 'active',
    type: '',
    search: '',
    organization: '',
    show_scheduled: 'true', // Show scheduled alerts in active tab by default
  },
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

const updateAlertInList = (list, updatedAlert) =>
  list.map((alert) => (alert.id === updatedAlert.id ? updatedAlert : alert));

export default function alertsReducer(state = initialState, action) {
  switch (action.type) {
    case actions.FETCH_ALERTS.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case actions.FETCH_ALERTS.SUCCESS: {
      const { data, meta } = action.payload;
      const list = Array.isArray(data) ? data : [];

      return {
        ...state,
        loading: false,
        list, // Backend data REPLACES local state completely
        pagination: meta ? { ...state.pagination, ...meta } : state.pagination,
      };
    }
    case actions.FETCH_ALERTS.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case actions.FETCH_ALERT_DETAIL.REQUEST:
      return {
        ...state,
        detailLoading: true,
        error: null,
      };
    case actions.FETCH_ALERT_DETAIL.SUCCESS:
      return {
        ...state,
        detailLoading: false,
        selectedAlert: action.payload.data,
      };
    case actions.FETCH_ALERT_DETAIL.FAILURE:
      return {
        ...state,
        detailLoading: false,
        error: action.payload,
      };

    case actions.RESOLVE_ALERT.REQUEST:
    case actions.DISMISS_ALERT.REQUEST:
    case actions.CREATE_ALERT.REQUEST:
    case actions.MARK_ALERT_READ.REQUEST:
    case actions.MARK_ALL_ALERTS_READ.REQUEST:
      return {
        ...state,
        actionLoading: true,
        error: null,
        success: null,
      };

    case actions.RESOLVE_ALERT.SUCCESS: {
      const updated = action.payload.data;
      return {
        ...state,
        actionLoading: false,
        success: 'resolved',
        list: updateAlertInList(state.list, updated),
        selectedAlert:
          state.selectedAlert && state.selectedAlert.id === updated.id
            ? updated
            : state.selectedAlert,
      };
    }
    case actions.DISMISS_ALERT.SUCCESS: {
      const updated = action.payload.data;
      return {
        ...state,
        actionLoading: false,
        success: 'dismissed',
        list: updateAlertInList(state.list, updated),
        selectedAlert:
          state.selectedAlert && state.selectedAlert.id === updated.id
            ? updated
            : state.selectedAlert,
      };
    }
    case actions.CREATE_ALERT.SUCCESS:
      return {
        ...state,
        actionLoading: false,
        success: 'created',
        list: [action.payload.data, ...state.list],
      };

    case actions.MARK_ALERT_READ.SUCCESS: {
      const updated = action.payload.data;
      return {
        ...state,
        actionLoading: false,
        success: 'read',
        list: updateAlertInList(state.list, updated),
        selectedAlert:
          state.selectedAlert && state.selectedAlert.id === updated.id
            ? updated
            : state.selectedAlert,
      };
    }

    case actions.MARK_ALL_ALERTS_READ.SUCCESS:
      return {
        ...state,
        actionLoading: false,
        success: 'all_read',
      };

    case actions.RESOLVE_ALERT.FAILURE:
    case actions.DISMISS_ALERT.FAILURE:
    case actions.CREATE_ALERT.FAILURE:
    case actions.MARK_ALERT_READ.FAILURE:
    case actions.MARK_ALL_ALERTS_READ.FAILURE:
      return {
        ...state,
        actionLoading: false,
        error: action.payload,
      };

    case 'ADD_NOTIFIED_ALERT': {
      // Add alert to notified list if not already there
      const currentNotified = Array.isArray(state.notifiedAlerts) ? state.notifiedAlerts : [];
      if (!currentNotified.includes(action.payload)) {
        return {
          ...state,
          notifiedAlerts: [...currentNotified, action.payload],
        };
      }
      return state;
    }

    case 'REMOVE_NOTIFIED_ALERT':
      // Remove alert from notified list
      return {
        ...state,
        notifiedAlerts: Array.isArray(state.notifiedAlerts)
          ? state.notifiedAlerts.filter(id => id !== action.payload)
          : [],
      };

    case 'CLEAR_NOTIFIED_ALERTS':
      // Clear all notified alerts
      return {
        ...state,
        notifiedAlerts: [],
      };

    case 'ADD_TO_POPOVER_ALERT_LIST': {
      // Add alert to popover list if not already there
      const currentPopoverList = Array.isArray(state.popoverAlertList) ? state.popoverAlertList : [];
      const alertExists = currentPopoverList.some(a => a.id === action.payload.id);
      if (!alertExists) {
        return {
          ...state,
          popoverAlertList: [...currentPopoverList, action.payload],
        };
      }
      return state;
    }

    case 'REMOVE_FROM_POPOVER_ALERT_LIST':
      // Remove alert from popover list
      return {
        ...state,
        popoverAlertList: Array.isArray(state.popoverAlertList)
          ? state.popoverAlertList.filter(alert => alert.id !== action.payload)
          : [],
      };

    case 'CLEAR_POPOVER_ALERT_LIST':
      // Clear all alerts from popover list
      return {
        ...state,
        popoverAlertList: [],
      };

    case actions.SET_ALERT_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    case actions.SET_SELECTED_ALERT:
      return {
        ...state,
        selectedAlert: action.payload,
      };

    case actions.CLEAR_ALERTS_FEEDBACK:
      return {
        ...state,
        success: null,
      };

    case actions.RESET_ALERTS_STATE:
      return initialState;

    case actions.REMOVE_ALERT_LOCALLY: {
      const alertId = action.payload;
      return {
        ...state,
        list: state.list.filter((alert) => alert.id !== alertId),
      };
    }

    case actions.ACTIVATE_NEXT_RECURRENCE.REQUEST:
      return {
        ...state,
        actionLoading: true,
        error: null,
      };

    case actions.ACTIVATE_NEXT_RECURRENCE.SUCCESS: {
      return {
        ...state,
        actionLoading: false,
        list: action.payload.alerts, // Replace with backend data
        success: action.payload.message,
      };
    }

    case actions.ACTIVATE_NEXT_RECURRENCE.FAILURE:
      return {
        ...state,
        actionLoading: false,
        error: action.payload,
      };

    case actions.FETCH_UNREAD_COUNT.SUCCESS:
      return {
        ...state,
        unreadCount: action.payload.count,
      };

    case actions.ADVANCE_BATCH_FROM_ALERT.REQUEST:
      return {
        ...state,
        actionLoading: true,
        error: null,
      };
    case actions.ADVANCE_BATCH_FROM_ALERT.SUCCESS:
      return {
        ...state,
        actionLoading: false,
        success: 'batch_advanced',
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case actions.ADVANCE_BATCH_FROM_ALERT.FAILURE:
      return {
        ...state,
        actionLoading: false,
        error: action.payload,
      };

    default:
      return state;
  }
}
